<script setup lang="ts">
import { NAvatar, NButton, NDrawer, NDropdown, NLayout, NLayoutContent, NLayoutHeader } from 'naive-ui'
import { computed, ref } from 'vue'
import { lighten, rgba } from 'polished'
import SiderStudent from '@/components/layout/sider/SiderStudent.vue'
import { SvgIcon } from '@/components/common'
import { useBasicLayout } from '@/hooks/useBasicLayout'
import { useAppStore, useAuthStore, useCreditStore, useUserStore } from '@/store'
import { useTheme } from '@/hooks/useTheme'
import logoDark from '@/assets/login/logoLoginDark.png'
import logoLight from '@/assets/login/logoLoginLight.png'
import defaultAvatar from '@/assets/avatar.jpg'
import { transformMinioUrl } from '@/utils/functions'
import TokenPill from '@/components/common/TokenPill.vue'
import { useAccountDropdown } from '@/composables/useAccountDropdown'
import FormSetPassword from '@/components/layout/sider/FormSetPassword.vue'
import FormChangePassword from '@/components/layout/sider/FormChangePassword.vue'
import FormUserProfile from '@/components/layout/sider/FormUserProfile.vue'
import FormClassInfo from '@/components/layout/sider/FormClassInfo.vue'
import FromDetailWorkspace from '@/views/workspace-settings/FromDetailWorkspace.vue'

const { isMobile, isTablet } = useBasicLayout()
const appStore = useAppStore()
const creditStore = useCreditStore()
const userStore = useUserStore()
const authStore = useAuthStore()
const { isDark } = useTheme()

const collapsed = ref<boolean>(false)
const showDrawer = ref(false)

// Show drawer menu for mobile/tablet
const isCompactView = computed(() => isMobile.value || isTablet.value)

// User info and credits for mobile header
const userInfo = computed(() => userStore.userInfo)
const basicCredit = computed(() => creditStore.basicCreditBalance)
const advancedCredit = computed(() => creditStore.advancedCreditBalance)

// Account dropdown
const { dropdownOptions, handleSelect: handleAccountSelect } = useAccountDropdown()

// Form modals
const showSetPassword = ref(false)
const showChangePassword = ref(false)
const showUserProfile = ref(false)
const showClassInfo = ref(false)
const showDetailWorkspace = ref(false)

const onAccountSelect = (key: string) => {
  handleAccountSelect(key, {
    onViewProfile: () => { showUserProfile.value = true },
    onClassInfo: () => { showClassInfo.value = true },
    onSetPassword: () => {
      if (userStore?.isFirstLogin)
        showSetPassword.value = true
      else
        showChangePassword.value = true
    },
    onLogout: async () => { await authStore.logout() },
  })
}

const onCloseSetPassword = async (reload: boolean) => {
  showSetPassword.value = false
  if (reload)
    await authStore.logout()
}

const onCloseChangePassword = async (reload: boolean) => {
  showChangePassword.value = false
  if (reload)
    await authStore.logout()
}

const primaryColor = computed(() => appStore.colorTheme || '#3b82f6')
const primaryColorLight = computed(() => lighten(0.1, primaryColor.value))
const primaryColorRgba = computed(() => rgba(primaryColor.value, 0.1))

const getMobileClass = computed(() => {
  return isMobile.value
    ? ['rounded-none', 'shadow-none']
    : ['border', 'rounded-md', 'shadow-md', 'dark:border-neutral-800']
})

const getContainerClass = computed(() => {
  if (isCompactView.value)
    return ['min-h-screen']

  return [
    'min-h-screen',
    !collapsed.value ? 'pl-[260px]' : 'pl-[70px]',
  ]
})

const updateCollapsed = (value: any) => {
  collapsed.value = !value
}

const toggleDrawer = () => {
  showDrawer.value = !showDrawer.value
}
</script>

<template>
  <div
    class="min-h-screen transition-all duration-300 p-0 bg-primary"
  >
    <div class="min-h-screen relative z-10" :class="getMobileClass">
      <NLayout :has-sider="!isCompactView" class="z-40 transition-all duration-300" :class="getContainerClass">
        <!-- Desktop Sider -->
        <SiderStudent v-if="!isCompactView" :collapsed="collapsed" @update:collapsed="updateCollapsed" />

        <NLayout>
          <!-- Mobile/Tablet Header with Hamburger -->
          <NLayoutHeader v-if="isCompactView" class="mobile-header" bordered>
            <div class="flex items-center justify-between px-4 py-3 gap-3">
              <!-- Left: Hamburger + Logo -->
              <div class="flex items-center gap-2">
                <NButton
                  quaternary
                  circle
                  @click="toggleDrawer"
                >
                  <template #icon>
                    <SvgIcon icon="ion:menu-outline" size="24" />
                  </template>
                </NButton>
                <RouterLink to="/agent-management" class="flex items-center">
                  <img
                    :src="isDark ? logoDark : logoLight"
                    alt="Logo"
                    class="h-8"
                  >
                </RouterLink>
              </div>

              <!-- Right: Credits and Avatar -->
              <div class="flex items-center gap-3 ml-auto">
                <!-- Tablet: full pill, Mobile: compact pill (只图标) -->
                <TokenPill
                  :credits="basicCredit"
                  :points="advancedCredit"
                  :compact="isMobile"
                />
                <NDropdown
                  trigger="click"
                  :options="dropdownOptions"
                  placement="bottom-end"
                  @select="onAccountSelect"
                >
                  <NAvatar
                    round
                    :size="32"
                    :src="userInfo.icon_url ? transformMinioUrl(userInfo.icon_url) : defaultAvatar"
                    class="cursor-pointer"
                  />
                </NDropdown>
              </div>
            </div>
          </NLayoutHeader>

          <NLayoutContent class="content-area">
            <div class="page-container" :class="{ compact: isCompactView }">
              <RouterView v-slot="{ Component, route: routerRoute }">
                <template v-if="Component">
                  <Transition name="page-transition" mode="out-in" appear>
                    <div :key="routerRoute.path" class="route-wrapper">
                      <Suspense>
                        <component :is="Component" />
                      </Suspense>
                    </div>
                  </Transition>
                </template>
              </RouterView>
            </div>
          </NLayoutContent>
        </NLayout>
      </NLayout>

      <!-- Mobile/Tablet Drawer -->
      <NDrawer
        v-model:show="showDrawer"
        :width="260"
        placement="left"
        :trap-focus="false"
        :block-scroll="false"
      >
        <SiderStudent :is-drawer="true" :collapsed="false" @menu-click="showDrawer = false" />
      </NDrawer>
    </div>

    <!-- Account Modals -->
    <FormSetPassword
      v-if="showSetPassword"
      :show="showSetPassword"
      :email="userInfo.email"
      @on-close="onCloseSetPassword"
    />
    <FormChangePassword
      v-if="showChangePassword"
      :show="showChangePassword"
      @on-close="onCloseChangePassword"
    />
    <FormUserProfile
      v-if="showUserProfile"
      :show="showUserProfile"
      @on-close="(reload: boolean) => { showUserProfile = false }"
    />
    <FormClassInfo
      v-if="showClassInfo"
      :show="showClassInfo"
      @on-close="(reload: boolean) => { showClassInfo = false }"
    />
    <FromDetailWorkspace
      v-if="showDetailWorkspace"
      :show="showDetailWorkspace"
      @on-close="showDetailWorkspace = false"
    />
  </div>
</template>

<style scoped>
/* Gradient Animation */
@keyframes gradient-x {
	0%,
	100% {
		background-position: 0% 50%;
	}

	50% {
		background-position: 100% 50%;
	}
}

.animate-gradient-x {
	background-size: 200% 200%;
	animation: gradient-x 3s ease infinite;
}

/* Slow Spin Animation */
@keyframes spin-slow {
	from {
		transform: rotate(0deg);
	}

	to {
		transform: rotate(360deg);
	}
}

.animate-spin-slow {
	animation: spin-slow 8s linear infinite;
}

/* Header Glassmorphism */
.header-glassmorphism {
	position: relative;
	z-index: 50;
}

.header-glassmorphism::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
	backdrop-filter: blur(20px);
	border-radius: 0;
	z-index: -1;
}

/* Modern Action Buttons with dynamic colors */
.action-button-modern {
	@apply relative flex items-center justify-center w-9 h-9 rounded-2xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 text-gray-600 dark:text-gray-300 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md hover:scale-105;
}

.action-button-modern:hover {
	color: var(--primary-color);
	background: rgba(255, 255, 255, 0.9);
}

.dark .action-button-modern:hover {
	background: rgba(55, 65, 81, 0.9);
}

.action-button-modern:hover::before {
	content: '';
	position: absolute;
	inset: 0;
	background: linear-gradient(135deg, var(--primary-color), rgba(147, 51, 234, 0.1));
	opacity: 0.1;
	border-radius: 0.75rem;
	z-index: -1;
}

/* Profile Avatar Container with dynamic colors */
.profile-avatar-container {
	@apply cursor-pointer relative;
}

.profile-avatar-container:hover::before {
	content: '';
	position: absolute;
	inset: -4px;
	background: conic-gradient(from 180deg, var(--primary-color), transparent, var(--primary-color));
	border-radius: 50%;
	z-index: -1;
	animation: pulse 2s infinite;
	opacity: 0.3;
}

/* Mobile Header */
.mobile-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 110;
  background: white;
}

.page-container.compact {
  padding-top: 64px; /* header height */
}

.dark .mobile-header {
  background: #1e1e2d;
}

/* Breadcrumb Wrapper Styles */
.breadcrumb-wrapper {
	@apply px-3 py-3;
}

/* Content and Page Container */
.content-area {
	/*@apply h-full overflow-hidden;*/
}

/* Content Area with dynamic gradients */
.content-area {
	/*height: calc(100vh - 64px);*/
	overflow: auto;
	position: relative;
	display: flex;
	flex-direction: column;
	flex: 1 1 0%;
}

/* Content Area with dynamic gradients - full screen */
.content-area::before {
	content: '';
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background:
		radial-gradient(circle at 20% 20%, v-bind('primaryColorRgba') 0%, transparent 50%),
		radial-gradient(circle at 80% 80%, v-bind('rgba(primaryColorLight, 0.1)') 0%, transparent 50%),
		radial-gradient(circle at 40% 60%, rgba(16, 185, 129, 0.05) 0%, transparent 50%);
	pointer-events: none;
	z-index: 0;
}

.content-area>div {
	position: relative;
	z-index: 1;
}

/* Page Container - ensures single root element for transitions */
.page-container {
	width: 100%;
	height: 100%;
	min-height: 100%;
	position: relative;
}

.route-wrapper {
	width: 100%;
	height: 100%;
	min-height: 100%;
	display: block;
	position: relative;
	overflow: auto;
}

/* Ensure child components take full space */
.route-wrapper>* {
	width: 100%;
	/* min-height: 100%; */
}

/* Layout Transitions */
:deep(.n-layout .n-layout-scroll-container) {
	/*height: auto;*/
	display: flex;
	flex-direction: column;
}

/* Page Transitions - Simple fade and slide */
.page-transition-enter-active {
	transition: all 0.3s ease-out;
}

.page-transition-leave-active {
	transition: all 0.2s ease-in;
}

.page-transition-enter-from {
	opacity: 0;
	transform: translateX(20px);
}

.page-transition-leave-to {
	opacity: 0;
	transform: translateX(-20px);
}

.page-transition-enter-to,
.page-transition-leave-from {
	opacity: 1;
	transform: translateX(0);
}

/* Enhanced Scrollbar with dynamic colors */
.content-area::-webkit-scrollbar {
	width: 6px;
}

.content-area::-webkit-scrollbar-track {
	background: transparent;
}

.content-area::-webkit-scrollbar-thumb {
	background: linear-gradient(135deg, v-bind('rgba(primaryColor, 0.3)'), v-bind('rgba(primaryColorLight, 0.3)'));
	border-radius: 3px;
}

.content-area::-webkit-scrollbar-thumb:hover {
	background: linear-gradient(135deg, v-bind('rgba(primaryColor, 0.5)'), v-bind('rgba(primaryColorLight, 0.5)'));
}

/* Responsive adjustments */
@media (max-width: 768px) {
	.action-button-modern {
		@apply w-8 h-8;
	}

	.profile-avatar-container .w-10 {
		@apply w-8 h-8;
	}
}
</style>
